<script lang="ts">
  import { onMount } from 'svelte';
  import { getOrganization, listClients } from '@shared/api/admin';
  import { deactivateAccount, reactivateAccount, revokeCredentials } from '@shared/api/accounts';
  import { cancelInvitation, removeMemberFromOrg } from '@shared/api/members';
  import ConfirmDialog from '@shared/ui/ConfirmDialog.svelte';
  import type { Auth0Client, Organization } from '@shared/types/admin';
  import { normalizeAccountRole } from '@shared/types/account';
  import type { Member } from '@shared/types/member';
  import { memberListStore } from '@entities/member/store.svelte';
  import MemberTable from '@widgets/member-table/MemberTable.svelte';
  import RoleChangeDialog from '@features/member-role-change/RoleChangeDialog.svelte';
  import InviteDialog from '@features/member-invite/InviteDialog.svelte';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import {
    canManageMembers,
    canManageMemberWithRole,
    canManageTenant,
    canViewMembers,
  } from '@shared/types/session';
  import { navigate } from '@shared/router/navigation';

  interface Props {
    id: string;
  }
  const { id }: Props = $props();

  let org = $state<Organization | null>(null);
  let clients = $state<Auth0Client[]>([]);
  // 招待元として選べるのは Organizations 有効な SPA のみ。M2M (non_interactive) 等を
  // 選ぶと Auth0 が `client_id ... does not allow organizations` で 400 を返すため、
  // 候補・デフォルトとも app_type === 'spa' に絞る。
  let spaClients = $derived(clients.filter((c) => c.app_type === 'spa'));
  let inviteClientId = $derived(spaClients[0]?.client_id ?? null);

  let loading = $state(false);
  let error = $state<string | null>(null);

  // 招待モーダルの開閉。ロール選択 / 送信は InviteDialog (features) に委譲する。
  let showInvite = $state(false);

  // ロール変更モーダルの対象 (RoleChangeDialog を開く)。org スコープはこのページの id。
  let roleTarget = $state<Member | null>(null);

  // 確認ダイアログ (無効化 / 再有効化 / 招待取消 / 組織から外す / 認証情報削除)。
  // window.confirm/alert を置換し、確認 → 実行 (loading) → 失敗時エラー表示を内包する。
  type ConfirmAction = {
    title: string;
    message: string;
    confirmLabel: string;
    danger: boolean;
    run: () => Promise<void>;
  };
  let confirmAction = $state<ConfirmAction | null>(null);

  // 権限の UI ミラー (真実は backend)。メンバー画面と同じゲートを使う:
  // - 閲覧 / 招待 / ロール変更 / 招待取消: super_admin / org_admin (AdminInviteOrgMember 等)。
  // - 無効化 / 除名: super_admin 専用 (AdminManageGlobal)。
  let allowView = $derived(!!sessionStore.user && canViewMembers(sessionStore.user.role));
  let allowManageMembers = $derived(
    !!sessionStore.user && canManageMembers(sessionStore.user.role),
  );
  let allowManageTenant = $derived(!!sessionStore.user && canManageTenant(sessionStore.user.role));
  // 招待 / 招待取消は org スコープ操作なので org_admin も自組織分は可。除名は super_admin 据え置き。
  let canInvite = $derived(allowManageMembers && !!inviteClientId);

  async function refresh() {
    loading = true;
    error = null;
    try {
      const [orgData, clientData] = await Promise.all([getOrganization(id), listClients()]);
      org = orgData;
      clients = clientData;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
    // メンバー一覧は統一ビュー (app_accounts 主役 + Auth0 相関) を org スコープで取得する。
    // メンバー画面と同じ store / ウィジェットを共有し、表示の食い違いをなくす。
    if (allowView) void memberListStore.refresh(id);
  }

  onMount(() => {
    void refresh();
  });

  /// この行のメンバーのロールを現在のユーザーが変更できるか (被害者保護ガードの UI ミラー)。
  function canChangeRoleOf(member: Member): boolean {
    const user = sessionStore.user;
    if (!user) return false;
    return canManageMemberWithRole(user.role, normalizeAccountRole(member.current_role));
  }

  function handleRemoveFromOrg(member: Member) {
    confirmAction = {
      title: '組織から外す',
      message: `${member.display_name} を組織から外します。担当者マスタと Auth0 ユーザー自体は残ります。`,
      confirmLabel: '組織から外す',
      danger: true,
      run: async () => {
        await removeMemberFromOrg(member.id, id);
        await memberListStore.refresh(id);
      },
    };
  }

  function handleDeactivate(member: Member) {
    confirmAction = {
      title: '担当者を無効化',
      message: `"${member.display_name}" を無効化します。\nログインを停止し新規割り当ての候補から外れますが、履歴上の氏名は残り、再有効化で戻せます。`,
      confirmLabel: '無効化する',
      danger: true,
      run: async () => {
        await deactivateAccount(member.id);
        await memberListStore.refresh(id);
      },
    };
  }

  function handleReactivate(member: Member) {
    confirmAction = {
      title: '担当者を再有効化',
      message: `"${member.display_name}" を再有効化します。ログインを再び許可します。`,
      confirmLabel: '再有効化する',
      danger: false,
      run: async () => {
        await reactivateAccount(member.id);
        await memberListStore.refresh(id);
      },
    };
  }

  function handleCancelInvitation(member: Member) {
    confirmAction = {
      title: '招待を取り消す',
      message: `${member.display_name} への招待を取り消します。担当者マスタの招待行も削除されます。`,
      confirmLabel: '招待を取り消す',
      danger: true,
      run: async () => {
        await cancelInvitation(member.id, id);
        await memberListStore.refresh(id);
      },
    };
  }

  function handleRevokeCredentials(member: Member) {
    confirmAction = {
      title: '認証情報を削除',
      message: `"${member.display_name}" の認証情報を削除します。\nAuth0 ユーザーを削除しログインを恒久的に無効化します。商談・契約・履歴上の氏名は残ります。\nこの操作は元に戻せません。`,
      confirmLabel: '認証情報を削除する',
      danger: true,
      run: async () => {
        await revokeCredentials(member.id);
        await memberListStore.refresh(id);
      },
    };
  }
</script>

<header class="settings-header">
  <p class="eyebrow">
    <a
      href="/admin/organizations"
      onclick={(ev) => {
        ev.preventDefault();
        navigate('/admin/organizations');
      }}>← 組織一覧</a
    >
  </p>
  <h1>
    {#if org}
      {org.display_name} <small class="muted">({org.name})</small>
    {:else}
      組織詳細
    {/if}
  </h1>
</header>

{#if loading && org === null}
  <p class="muted">読み込み中…</p>
{:else if error}
  <div class="error">{error}</div>
{:else if org !== null}
  <section class="panel">
    <div class="panel-head">
      <h2>メンバー <small class="muted">({memberListStore.items.length})</small></h2>
      <button
        type="button"
        class="btn btn-primary"
        disabled={!canInvite}
        title={allowManageMembers
          ? canInvite
            ? ''
            : '招待元 SPA client が解決できていません'
          : '権限がありません'}
        onclick={() => (showInvite = true)}
      >
        + メンバーを招待
      </button>
    </div>
    {#if !allowView}
      <p class="muted">メンバーを表示する権限がありません。</p>
    {:else if memberListStore.loading && memberListStore.items.length === 0}
      <p class="muted">読み込み中…</p>
    {:else if memberListStore.error}
      <div class="error">{memberListStore.error}</div>
    {:else if memberListStore.items.length === 0}
      <p class="muted">メンバーはまだ登録されていません。</p>
    {:else}
      <MemberTable
        members={memberListStore.items}
        {allowManageMembers}
        {allowManageTenant}
        {canChangeRoleOf}
        currentAccountId={sessionStore.accountId}
        onChangeRole={(m) => (roleTarget = m)}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onRemoveFromOrg={handleRemoveFromOrg}
        onCancelInvitation={handleCancelInvitation}
        onRevokeCredentials={handleRevokeCredentials}
      />
    {/if}
  </section>
{/if}

{#if showInvite}
  <InviteDialog
    orgId={id}
    clientId={inviteClientId}
    clients={spaClients}
    onClose={() => (showInvite = false)}
    onInvited={() => memberListStore.refresh(id)}
  />
{/if}

{#if roleTarget}
  <RoleChangeDialog
    member={roleTarget}
    orgId={id}
    onClose={() => (roleTarget = null)}
    onChanged={() => memberListStore.refresh(id)}
  />
{/if}

{#if confirmAction}
  <ConfirmDialog
    title={confirmAction.title}
    message={confirmAction.message}
    confirmLabel={confirmAction.confirmLabel}
    danger={confirmAction.danger}
    onConfirm={confirmAction.run}
    onClose={() => (confirmAction = null)}
  />
{/if}

<style>
  .settings-header {
    margin-bottom: 16px;
  }
  .eyebrow {
    margin: 0 0 2px;
    color: var(--muted);
    font-weight: 700;
  }
  .eyebrow a {
    color: inherit;
  }
  .panel {
    padding: 20px;
    margin-bottom: 16px;
  }
  .panel h2 {
    margin: 0 0 12px;
  }
  .panel h2 small {
    font-weight: 400;
    font-size: 14px;
  }
  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }
  .panel-head h2 {
    margin: 0;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin: 8px 0;
  }
</style>
